import React from 'react'
import { useTheme } from '@emotion/react'
import { Box, Card, Grid, Typography, useMediaQuery } from '@mui/material'
import { CalendarMonthOutlined, DescriptionOutlined } from '@mui/icons-material'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { containerRef } from 'utils'
import NoReports from '../../reports/NoReports'
import { useSelector } from 'react-redux'
import { FetchAllReportsShared } from 'api/report'
import { Secondary } from 'ui-components/Buttons'
import ReportDetailPopup from './ReportDetailPopup'
import ImageAvatar from 'ui-components/ImageAvatar'
import ReportsSkeleton from '../../reports/ReportsSkeleton';

// Report card icon — ported verbatim from the senior's live startup-web reports page
// (views/reports/Reports.js `ReportIcon`): a gradient-bordered tile with an outlined
// page + 3 gradient text lines. Gradient ids are per-instance (useId) because the icon
// repeats once per card in the list.
const ReportIcon = () => {
    const uid = React.useId();
    const g = (n) => `report_icon_${uid}_${n}`;
    const lines = [18, 24, 29];
    return (
        <Box
            component="svg"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            sx={{ flexShrink: 0, width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 } }}
        >
            <rect x="0.5" y="0.5" width="47" height="47" rx="13.5" stroke={`url(#${g('border')})`} />
            <rect x="13.5" y="10.5" width="20" height="27" rx="3.5" stroke={`url(#${g('page')})`} />
            {lines.map((y) => (
                <rect key={y} x="18" y={y} width="10" height="1" rx="0.5" fill="white" stroke={`url(#${g('line' + y)})`} />
            ))}
            <defs>
                <linearGradient id={g('border')} x1="24" y1="0" x2="24" y2="48" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7852F8" />
                    <stop offset="1" stopColor="#84AEF6" />
                </linearGradient>
                <linearGradient id={g('page')} x1="23.5" y1="10" x2="23.5" y2="38" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7852F8" />
                    <stop offset="1" stopColor="#84AEF6" />
                </linearGradient>
                {lines.map((y) => (
                    <linearGradient key={y} id={g('line' + y)} x1="23" y1={y - 0.5} x2="23" y2={y + 1.5} gradientUnits="userSpaceOnUse">
                        <stop stopColor="#7852F8" />
                        <stop offset="1" stopColor="#84AEF6" />
                    </linearGradient>
                ))}
            </defs>
        </Box>
    );
};

// Strip HTML so the rich-text `overview` renders as a plain AI-summary paragraph.
const stripHtml = (html) =>
    String(html ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const Reports = ({ startup_details_id }) => {
    const theme = useTheme()
    const isNotLargeScreen = useMediaQuery(theme.breakpoints.down('md'));
    const userDetails = useSelector((state) => state?.investorDetails?.data);
    const { data: reportsData, isLoading: isLoadingReports } = FetchAllReportsShared({ investor_id: userDetails?._id, queryParams: { startup_id: startup_details_id } });

    // "Open Report" -> detail popup. The whole report object is passed so the popup can render
    // immediately; it also refreshes via FetchReportDetails inside.
    const [selectedReport, setSelectedReport] = React.useState(null);
    const handleOpenReport = (report) => setSelectedReport(report);

    const stripHtml = (html) =>
        String(html ?? '')
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

    const ReportCard = ({ data: cardData }) => {
        const period = [cardData?.formatted_from_date, cardData?.formatted_to_date].filter(Boolean).join(' – ');
        const description = stripHtml(cardData?.overview);
        const documents = cardData?.documents || [];

        return (
            <Grid item xs={12} sm={6} lg={4}>
                <Box
                    onClick={() => handleOpenReport(cardData)}
                    sx={{
                        bgcolor: '#fff',
                        border: `1px solid #EAECF0`,
                        borderRadius: '12px',
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                        '&:hover': { boxShadow: '0px 4px 16px rgba(16,24,40,0.06)', borderColor: '#C7D7FE' },
                    }}
                >
                    {/* ---- Header: Avatar + startup name ---- */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <ImageAvatar
                            src={cardData?.image}
                            alt={cardData?.name}
                            width={36}
                            height={36}
                            style={{ width: 36, height: 36, borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#101828' }}>
                                {cardData?.name}
                            </Typography>
                        </Box>
                    </Box>

                    {/* ---- Report title ---- */}
                    {cardData?.report_name && (
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#101828' }}>
                            {cardData.report_name}
                        </Typography>
                    )}

                    {/* ---- Description ---- */}
                    {description && (
                        <Typography
                            sx={{
                                fontSize: '0.78rem',
                                color: '#98A2B3',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {description}
                        </Typography>
                    )}

                    {/* ---- Footer: date + document count ---- */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto', pt: 0.5, minWidth: 0 }}>
                        {period && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                <CalendarMonthOutlined sx={{ fontSize: 14, color: '#667085' }} />
                                <Typography noWrap sx={{ fontSize: '0.72rem', color: '#667085' }}>
                                    {period}
                                </Typography>
                            </Box>
                        )}
                        {documents.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DescriptionOutlined sx={{ fontSize: 14, color: '#667085' }} />
                                <Typography noWrap sx={{ fontSize: '0.72rem', color: '#667085' }}>
                                    {documents.length} {documents.length === 1 ? 'document' : 'documents'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>
        )
    }

    const list = reportsData;

    return (
        <>
            {
                isLoadingReports ?
                    <PerfectScrollbar
                        containerRef={containerRef}
                        style={{ height: "calc(100vh - 120px)" }}>
                        <Box padding={"2rem"} paddingTop={0}>
                            <ReportsSkeleton count={6} />
                        </Box>
                    </PerfectScrollbar>
                    :
                    <>
                        {list && list.length > 0 ?
                            <PerfectScrollbar
                                containerRef={containerRef}
                                style={{ height: "calc(100vh - 120px)" }}>
                                <Grid container xs={12} padding={"2rem"} paddingTop={isNotLargeScreen ? 0 : 0} spacing={2}>
                                    {list.map((report, index) => <ReportCard data={report} key={index} />)}
                                </Grid>
                            </PerfectScrollbar>
                            :
                            <NoReports />
                        }
                    </>}

            {selectedReport && (
                <ReportDetailPopup report={selectedReport} onClose={() => setSelectedReport(null)} />
            )}
        </>
    )
}

export default Reports
